import React, { Component } from 'react';
import { EditableCellV2 } from './MedTableRenderer';
import { AutoComplete, DatePicker } from 'antd';
import moment, { Moment } from 'moment';
import { generateLabTestsFollowUpOptions, randomString } from '../../../../../store/utils';
import {
    CREATE_DENTAL_EXAMINATIONS_SINCE_UNITS,
    CREATE_DENTAL_PROCEDURES_START_ON_UNITS,
    CREATE_LAB_TESTS_REPEAT_ON_UNITS,
    CREATE_LAB_TESTS_TEST_ON_UNITS,
    LAB_TESTS_FOLLOWUP_DATE_TYPE,
    LAB_TESTS_FOLLOWUP_TIME_TYPE,
} from '../../../../common/optionGeneratorUtils';
import {
    DentalExamination,
    DentalProcedure,
    LabTestsEntityOrProceduresEntity,
    MedicationsEntity,
} from '../../../../../store/types/prescription';
import { track } from '../../../../../tracking';

export enum LAB_TESTS_FOLLOWUP_TYPE {
    TEST_ON = 'test_on',
    REPEAT_ON = 'repeat_on',
    START_ON = 'start_on',
    SINCE = 'since',
    DATE = 'date',
}

interface Props {
    date?: string;
    upsertElement: (
        element:
            | MedicationsEntity
            | LabTestsEntityOrProceduresEntity
            | DentalExamination
            | DentalProcedure,
        index?: number | undefined,
        calculateQuantity?: boolean | undefined,
    ) => void;
    elements: MedicationsEntity[];
    el: any;
    containerId: string;
    type: LAB_TESTS_FOLLOWUP_TYPE;
    defaultOptions: labTestFollowUpUnitTypeWithTimeValue[];
    disableFutureDate?: boolean;
}

type State = {
    options: labTestFollowUpUnitTypeWithTimeValue[];
};

export type labTestFollowUpUnitType = {
    label: string;
    type: LAB_TESTS_FOLLOWUP_DATE_TYPE;
    timeType: LAB_TESTS_FOLLOWUP_TIME_TYPE;
    value: string;
};

type labTestFollowUpUnitTypeWithTimeValue = labTestFollowUpUnitType & {
    timeValue: string;
};

export const isExact12 = (date: string) => {
    const myDate = moment.utc(date || '').utcOffset('+05:30');
    return myDate.format('HH:mm:ss') === '12:00:00';
};

const typeToDateFormat: { [k: string]: string } = {
    [LAB_TESTS_FOLLOWUP_TYPE.START_ON]: 'Do MMM YY',
    [LAB_TESTS_FOLLOWUP_TYPE.DATE]: 'Do MMM YY',
};

export const buildFollowUpLabel = (date?: string, type?: string) => {
    if (!date) {
        return '';
    }

    if (isExact12(date)) {
        const dateFormat = typeToDateFormat[type || ''] || 'Do MMMM YY';
        return (
            moment
                .utc(date || '')
                .utcOffset('+05:30')
                .format(dateFormat) || ''
        );
    }

    return (
        moment
            .utc(date || '')
            .utcOffset('+05:30')
            .format('Do MMMM YY, h:mm A') || ''
    );
};

export const timeObject = { hour: 12, minute: 0, second: 0, millisecond: 0 };

export class LabTestsDateSelector extends Component<Props, State> {
    testOnRef: React.RefObject<any>;

    constructor(props: Props) {
        super(props);
        this.state = {
            options: [],
        };
        this.testOnRef = React.createRef();
    }

    ceilMinutesToNearest30Minutes(isoString: string) {
        const time = moment.utc(isoString).utcOffset('+05:30');
        const minutes = time.minutes();

        if (minutes < 15) {
            time.minutes(0).seconds(1); // 1 second is to prevent exact 12 noon time
        } else if (15 <= minutes && minutes <= 45) {
            time.minutes(30).seconds(1);
        } else {
            time.minutes(60).seconds(1);
        }

        return time.toISOString();
    }

    onLabTestFollowUpSearch = (text: string, type: LAB_TESTS_FOLLOWUP_TYPE) => {
        const options = generateLabTestsFollowUpOptions(
            type === LAB_TESTS_FOLLOWUP_TYPE.SINCE
                ? CREATE_DENTAL_EXAMINATIONS_SINCE_UNITS
                : type === LAB_TESTS_FOLLOWUP_TYPE.START_ON || type === LAB_TESTS_FOLLOWUP_TYPE.DATE
                ? CREATE_DENTAL_PROCEDURES_START_ON_UNITS
                : type === LAB_TESTS_FOLLOWUP_TYPE.TEST_ON
                ? CREATE_LAB_TESTS_TEST_ON_UNITS
                : CREATE_LAB_TESTS_REPEAT_ON_UNITS,
            text,
        ).map((labTest) => ({
            ...labTest,
            value: randomString(8),
            timeValue: labTest.value,
        }));

        this.setState({ options: options });
    };

    onLabTestsFollowUpSelect = (
        medIndex: number,
        type: LAB_TESTS_FOLLOWUP_TYPE,
        option?: labTestFollowUpUnitTypeWithTimeValue,
    ) => {
        const { elements, upsertElement } = this.props;
        const element = elements[medIndex] as LabTestsEntityOrProceduresEntity;

        if (!option) {
            upsertElement({ ...element, [type]: undefined });
            return;
        }

        let fDate: string;

        if (option.timeType === LAB_TESTS_FOLLOWUP_TIME_TYPE.MID_DAY) {
            if (type === LAB_TESTS_FOLLOWUP_TYPE.SINCE) {
                fDate = moment()
                    .subtract(option?.timeValue, option.type)
                    .set(timeObject)
                    .toISOString();
            } else {
                fDate = moment().add(option?.timeValue, option.type).set(timeObject).toISOString();
            }
        } else if (
            option.timeType === LAB_TESTS_FOLLOWUP_TIME_TYPE.YESTERDAY &&
            type === LAB_TESTS_FOLLOWUP_TYPE.SINCE
        ) {
            fDate = moment().subtract(1, 'days').set(timeObject).toISOString();
        } else if (
            option.timeType === LAB_TESTS_FOLLOWUP_TIME_TYPE.DAY_BEFORE_YESTERDAY &&
            type === LAB_TESTS_FOLLOWUP_TYPE.SINCE
        ) {
            fDate = moment().subtract(2, 'days').set(timeObject).toISOString();
        } else if (
            option.timeType === LAB_TESTS_FOLLOWUP_TIME_TYPE.TOMORROW &&
            (type === LAB_TESTS_FOLLOWUP_TYPE.START_ON || type === LAB_TESTS_FOLLOWUP_TYPE.DATE)
        ) {
            fDate = moment().add(1, 'days').set(timeObject).toISOString();
        } else if (
            option.timeType === LAB_TESTS_FOLLOWUP_TIME_TYPE.DAY_AFTER_TOMORROW &&
            (type === LAB_TESTS_FOLLOWUP_TYPE.START_ON || type === LAB_TESTS_FOLLOWUP_TYPE.DATE)
        ) {
            fDate = moment().add(2, 'days').set(timeObject).toISOString();
        } else {
            if (type === LAB_TESTS_FOLLOWUP_TYPE.SINCE) {
                fDate = this.ceilMinutesToNearest30Minutes(
                    moment().subtract(option?.timeValue, option.type).toISOString(),
                );
            } else {
                fDate = this.ceilMinutesToNearest30Minutes(
                    moment().add(option?.timeValue, option.type).toISOString(),
                );
            }
        }

        upsertElement({
            ...element,
            [type]: fDate,
        });

        track('LAB_TEST_FOLLOW_UP', {
            type: type,
        });
    };

    onCalendarChange = (date?: string) => {
        if (!date || !moment(date).isValid()) {
            return;
        }

        const fDate = moment(date);
        return fDate.set(timeObject).toISOString();
    };

    onLabTestsFollowUpCalenderSelection = (
        medIndex: number,
        type: LAB_TESTS_FOLLOWUP_TYPE,
        time: Moment | null,
    ) => {
        const date = this.onCalendarChange(time?.toISOString());
        const element = this.props.elements[medIndex];

        if (!date) {
            return;
        }

        this.props.upsertElement({ ...element, [type]: date });
    };

    disabledPastDate = (current: Moment): boolean => {
        const { disableFutureDate, type } = this.props;
        if (type === LAB_TESTS_FOLLOWUP_TYPE.START_ON || type === LAB_TESTS_FOLLOWUP_TYPE.DATE) {
            return false;
        }
        if (disableFutureDate) {
            return moment(current, 'YYYY-MM-DD').add(1, 'day') > moment(moment(), 'YYYY-MM-DD');
        }
        return moment(current, 'YYYY-MM-DD') < moment(moment(), 'YYYY-MM-DD');
    };

    render() {
        const { type, date, el, defaultOptions } = this.props;
        const { options } = this.state;

        const value = buildFollowUpLabel(date, type);

        return (
            <div>
                <div className="flex items-center space-x-4">
                    <div className="w-full">
                        <EditableCellV2
                            defaultValue={value || ''}
                            label={
                                type === LAB_TESTS_FOLLOWUP_TYPE.SINCE
                                    ? 'Since 3 Days'
                                    : type === LAB_TESTS_FOLLOWUP_TYPE.TEST_ON
                                    ? 'Today / After 3 Days'
                                    : 'After 3 Days'
                            }
                        >
                            <AutoComplete
                                ref={this.testOnRef}
                                className="bg-transparent"
                                style={{ width: '100%' }}
                                autoFocus
                                defaultOpen
                                allowClear
                                defaultValue={value || ''}
                                placeholder={
                                    type === LAB_TESTS_FOLLOWUP_TYPE.SINCE
                                        ? 'Since 3 Days'
                                        : type === LAB_TESTS_FOLLOWUP_TYPE.TEST_ON
                                        ? 'Today / After 3 Days'
                                        : 'After 3 Days'
                                }
                                options={options}
                                onSearch={(value) =>
                                    this.onLabTestFollowUpSearch(value as any, type)
                                }
                                onFocus={() => {
                                    if (!value) {
                                        this.setState({ options: defaultOptions });
                                    }
                                }}
                                onClear={() => {
                                    this.onLabTestsFollowUpSelect(el.medIndex, type, undefined);
                                }}
                                onSelect={(
                                    _: any,
                                    option: labTestFollowUpUnitTypeWithTimeValue,
                                ) => {
                                    this.setState({ options: [] });
                                    this.testOnRef?.current?.blur();
                                    this.onLabTestsFollowUpSelect(el.medIndex, type, option);
                                }}
                            />
                        </EditableCellV2>
                    </div>
                    <div>
                        <DatePicker
                            picker={'date'}
                            onChange={(t) =>
                                this.onLabTestsFollowUpCalenderSelection(el.medIndex, type, t)
                            }
                            allowClear={false}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            value={date ? moment(date) : null}
                            disabledDate={this.disabledPastDate}
                            format="YYYY-MM-DD"
                            getPopupContainer={() =>
                                this.props.containerId
                                    ? (document.getElementById(
                                          this.props.containerId,
                                      ) as HTMLElement)
                                    : document.body
                            }
                            className="test-on-repeat-on-date-picker"
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default LabTestsDateSelector;
